import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeftIcon, DocumentTextIcon, PaperAirplaneIcon } from "@heroicons/react/24/outline";
import { Button, Card, Container, PageHeader, Tag } from "../ui";
import { formatKigaliDate } from "../scholarships/model";
import { closesIn, studentStatusOf } from "../scholarships/student/model";

/**
 * One call as a student sees it, for scholarships and abroad loans alike: what it is about, each
 * programme's requirements and who may apply, the deadline, and either the student's application or
 * where to start one. Each page brings its data and its own `onStart(programmeId)`.
 *
 * `programmes` is [{ id, name, meta, requirements, whoMayApply }]; `beforeStart` and `afterStart`
 * are shown above and below the start button when the student has not applied. `preview`: staff
 * see a draft as students will, without the way back to the portal.
 */
export default function StudentCallView({ title, subtitle, aboutTitle, description, programmes, closesAt, open, mine, applicationPath, canStart, onStart, beforeStart, afterStart, preview = false }) {
  const navigate = useNavigate();
  const [programmeId, setProgrammeId] = useState("");
  const [starting, setStarting] = useState(false);
  const myStatus = mine && studentStatusOf(mine.studentStatus);

  const start = async () => {
    setStarting(true);
    try {
      await onStart(programmeId);
    } finally {
      setStarting(false);
    }
  };

  return (
    <Container className="flex flex-col gap-6 py-6">
      {!preview && (
        <Link to="/studentPortal" className="flex w-fit items-center gap-1 text-sm font-medium text-brand hover:underline">
          <ArrowLeftIcon className="h-4 w-4" /> Scholarships
        </Link>
      )}
      <PageHeader
        title={title}
        subtitle={subtitle}
        actions={
          mine && (
            <Button onClick={() => navigate(applicationPath(mine.id))} icon={<DocumentTextIcon className="h-4 w-4" />}>
              {mine.studentStatus === "DRAFT" ? "Continue my application" : "View my application"}
            </Button>
          )
        }
      />

      <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        <div className="flex flex-col gap-6">
          <Card title={aboutTitle}>
            <p className="whitespace-pre-line text-slate-700">{description}</p>
          </Card>
          <Card title="Choose a programme">
            <div className="flex flex-col gap-3">
              {programmes.map((p) => (
                <label
                  key={p.id}
                  className={`flex gap-3 rounded-lg border p-4 ${canStart ? "cursor-pointer hover:border-brand/40" : ""} ${programmeId === p.id ? "border-brand bg-brand/5" : "border-slate-200"}`}
                >
                  {canStart && <input type="radio" name="programme" className="mt-1 h-4 w-4 accent-brand" checked={programmeId === p.id} onChange={() => setProgrammeId(p.id)} />}
                  <div className="flex flex-col gap-2">
                    <div>
                      <p className="font-semibold text-slate-900">{p.name}</p>
                      <p className="text-sm text-slate-500">{p.meta}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {p.requirements.map((r) => (
                        <Tag key={r.id} color={r.mandatory ? "blue" : "gray"}>
                          {r.label}
                          {!r.mandatory && " (optional)"}
                        </Tag>
                      ))}
                    </div>
                    {p.whoMayApply}
                  </div>
                </label>
              ))}
            </div>
          </Card>
        </div>

        <div className="flex flex-col gap-4">
          <Card title="Deadline">
            <p className="text-2xl font-bold text-slate-900">{formatKigaliDate(closesAt)}</p>
            <p className="text-sm text-slate-500">{closesAt ? `23:59 Kigali time · ${closesIn(closesAt)}` : "No closing date yet"}</p>
          </Card>
          {mine ? (
            <Card title="Your application">
              <Tag color={myStatus.color}>{myStatus.label}</Tag>
              <p className="mt-2 text-sm text-slate-600">{myStatus.hint}</p>
            </Card>
          ) : (
            <>
              {beforeStart}
              {!open && <p className="text-sm text-slate-600">It is not accepting applications at the moment.</p>}
              {canStart && (
                <Button onClick={start} loading={starting} disabled={!programmeId} icon={<PaperAirplaneIcon className="h-4 w-4" />}>
                  {programmeId ? "Start my application" : "Choose a programme to apply"}
                </Button>
              )}
              {afterStart}
            </>
          )}
        </div>
      </div>
    </Container>
  );
}
